import CSSTools from "@adobe/css-tools";
import { NodeTypes } from "./node";

export function repeat(character: string, count: number) {
  return Array(count + 1).join(character)
}

export function trimLeadingNewlines(string: string) {
  return string.replace(/^\n*/, '')
}

export function trimTrailingNewlines(string: string) {
  // avoid match-at-end regexp bottleneck, see #370
  let indexEnd = string.length
  while (indexEnd > 0 && string[indexEnd - 1] === '\n') indexEnd--
  return string.substring(0, indexEnd)
}

export function trimNewlines(string: string) {
  return trimTrailingNewlines(trimLeadingNewlines(string))
}

export const blockElements = [
  'ADDRESS', 'ARTICLE', 'ASIDE', 'AUDIO', 'BLOCKQUOTE', 'BODY', 'CANVAS',
  'CENTER', 'DD', 'DIR', 'DIV', 'DL', 'DT', 'FIELDSET', 'FIGCAPTION', 'FIGURE',
  'FOOTER', 'FORM', 'FRAMESET', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'HEADER',
  'HGROUP', 'HR', 'HTML', 'ISINDEX', 'LI', 'MAIN', 'MENU', 'NAV', 'NOFRAMES',
  'NOSCRIPT', 'OL', 'OUTPUT', 'P', 'PRE', 'SECTION', 'TABLE', 'TBODY', 'TD',
  'TFOOT', 'TH', 'THEAD', 'TR', 'UL', 'X-TURNISH'
]

interface ParsedInlineStyle {
  source: string;
  declarations: Map<string, string>;
}

const inlineStyleCache = new WeakMap<Element, ParsedInlineStyle>();

const importantPattern = /\s*!important\s*$/i;

function parseInlineStyle(style: string): Map<string, string> {
  const declarations = new Map<string, string>();
  const o = CSSTools.parse('x {' + style + '}');
  const rule = o.stylesheet.rules[0];
  if (!rule || !('declarations' in rule) || !rule.declarations) {
    return declarations;
  }
  const importantProperties = new Set<string>();
  for (const decl of rule.declarations) {
    if (!('property' in decl) || !('value' in decl) || !decl.value) {
      continue;
    }
    const property = decl.property.toLowerCase();
    const isImportant = importantPattern.test(decl.value);
    if (importantProperties.has(property) && !isImportant) {
      continue;
    }
    if (isImportant) {
      importantProperties.add(property);
    }
    declarations.set(property, decl.value.replace(importantPattern, '').trim().toLowerCase());
  }
  return declarations;
}

function getInlineStyleProperty(node: Node, property: string): string | null {
  if (node.nodeType !== NodeTypes.Element) {
    return null;
  }
  const element = node as Element;
  const style = element.getAttribute('style');
  if (!style) {
    return null;
  }
  let parsed = inlineStyleCache.get(element);
  if (!parsed || parsed.source !== style) {
    parsed = { source: style, declarations: parseInlineStyle(style) };
    inlineStyleCache.set(element, parsed);
  }
  return parsed.declarations.get(property) ?? null;
}

const inlineDisplayValues = new Set([
  'inline', 'inline-block', 'inline-flex', 'inline-grid', 'contents', 'none',
]);

const blockDisplayValues = new Set([
  'block', 'flex', 'grid', 'table', 'list-item', 'flow-root',
]);

function getFlexDirection(element: Element): string {
  const flexDirection = getInlineStyleProperty(element, 'flex-direction');
  if (flexDirection) return flexDirection;

  const flexFlow = getInlineStyleProperty(element, 'flex-flow');
  if (flexFlow) {
    const parts = flexFlow.split(/\s+/);
    const directionValues = ['row', 'row-reverse', 'column', 'column-reverse'];
    for (const part of parts) {
      if (directionValues.includes(part)) return part;
    }
  }

  return 'row';
}

function getLayoutParent(node: Node): Element | null {
  let parent = node.parentNode;
  while (parent) {
    if (parent.nodeType !== NodeTypes.Element) {
      parent = parent.parentNode;
      continue;
    }
    const parentDisplay = getInlineStyleProperty(parent, 'display');
    if (parentDisplay !== 'contents') {
      return parent as Element;
    }
    parent = parent.parentNode;
  }
  return null;
}

function isRowItem(element: Element): boolean {
  const parent = getLayoutParent(element);
  if (!parent) return false;

  const parentDisplay = getInlineStyleProperty(parent, 'display');

  if (parentDisplay === 'flex' || parentDisplay === 'inline-flex') {
    const direction = getFlexDirection(parent);
    if (direction === 'row' || direction === 'row-reverse') {
      return true;
    }
  }

  return false;
}

const atomicInlineElements = ['BUTTON', 'METER', 'PROGRESS', 'SELECT', 'TEXTAREA'];

function isPlainInlineBox(element: Element): boolean {
  if (isRowItem(element)) {
    return false;
  }
  const display = getInlineStyleProperty(element, 'display');
  if (display) {
    return display === 'inline';
  }
  return !is(element, blockElements) && !is(element, atomicInlineElements);
}

export function isTransparentWrapper(node: Node): boolean {
  if (node.nodeType !== NodeTypes.Element) {
    return false;
  }
  const element = node as Element;
  return getInlineStyleProperty(element, 'display') === 'contents' || isPlainInlineBox(element);
}

function getFormattingParent(node: Node): Element | null {
  let parent = getLayoutParent(node);
  while (parent && isPlainInlineBox(parent)) {
    parent = getLayoutParent(parent);
  }
  return parent;
}

let blockCache = new WeakMap<Node, boolean>();

export function resetBlockCache(): void {
  blockCache = new WeakMap<Node, boolean>();
}

export function isBlock(node: Node): boolean {
  const cached = blockCache.get(node);
  if (cached !== undefined) {
    return cached;
  }
  const result = computeIsBlock(node);
  blockCache.set(node, result);
  return result;
}

function computeIsBlock(node: Node): boolean {
  if (node.nodeType === NodeTypes.Element) {
    const element = node as Element;

    const display = getInlineStyleProperty(element, 'display');
    if (display && inlineDisplayValues.has(display)) {
      return false;
    }

    if (isRowItem(element)) {
      return false;
    }

    const parent = getFormattingParent(element);
    if (parent && !isBlock(parent)) {
      return false;
    }

    if (display && blockDisplayValues.has(display)) {
      return true;
    }
  }

  return is(node, blockElements)
}

export const voidElements = [
  'AREA', 'BASE', 'BR', 'COL', 'COMMAND', 'EMBED', 'HR', 'IMG', 'INPUT',
  'KEYGEN', 'LINK', 'META', 'PARAM', 'SOURCE', 'TRACK', 'WBR'
]

export function isVoid(node: Node) {
  return is(node, voidElements)
}

export function hasVoid(node: Node) {
  return has(node, voidElements)
}

const meaningfulWhenBlankElements = [
  'A', 'TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TH', 'TD', 'IFRAME', 'SCRIPT',
  'AUDIO', 'VIDEO'
]

export const standardMarkdownElements = [
  'P', 'BR', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE',
  'UL', 'OL', 'LI', 'PRE', 'CODE', 'HR', 'A', 'EM', 'I', 'STRONG',
  'B', 'IMG', 'DIV', 'SPAN', 'TABLE', 'THEAD', 'TBODY', 'TR', 'TH', 'TD'
]

export function isMeaningfulWhenBlank(node: Node) {
  return is(node, meaningfulWhenBlankElements)
}

export function hasMeaningfulWhenBlank(node: Node) {
  return has(node, meaningfulWhenBlankElements)
}

function is(node: Node, tagNames: string[]) {
  return tagNames.indexOf(node.nodeName) >= 0
}

function has(node: Node, tagNames: string[]) {
  return (
    tagNames.some(function (tagName) {
      if (node.nodeType !== NodeTypes.Element) {
        return false;
      }
      return (node as Element).getElementsByTagName(tagName).length
    })
  )
}

export function sanitizedHtmlAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/'/g, '&#39;');
}

export function sanitizeWhitespace(string: string): string {
  return string ? string.replace(/(\n+\s*)+/g, '\n') : '';
}

export function sanitizedLinkContent(content: string): string {
  const sanitized = sanitizeWhitespace(content);
  return sanitized
    .replace(/[\t\r\n]+/g, ' ')
    .replace(/ {2,}/g, ' ')
    .trim();
}

const leadingBlockBreak = /^(?:[ \t]*\n){2,}/;
const trailingBlockBreak = /(?:\n[ \t]*){2,}$/;
const blockBreak = /\n[ \t]*\n/;

export function splitBlockEdges(content: string): { leading: string; body: string; trailing: string } {
  const leading = leadingBlockBreak.test(content) ? '\n\n' : '';
  const trailing = trailingBlockBreak.test(content) ? '\n\n' : '';
  return { leading, body: content.trim(), trailing };
}

const fencePattern = /^\s*(`{3,}|~{3,})/;
const unwrappableLinePattern = /^\s*(?:$|\||(?:[-*_]\s*){3,}$|=+\s*$)/;
const blockPrefixPattern = /^(\s*(?:>\s?)*(?:(?:[-+*]|\d+\.)\s+|#{1,6}\s+)?)(.*?)(\s*)$/;

export function wrapInlineContent(content: string, wrap: (text: string) => string): string {
  const { leading, body, trailing } = splitBlockEdges(content);
  if (!body) {
    return '';
  }
  if (!blockBreak.test(content)) {
    return leading + wrap(body) + trailing;
  }

  let fence: string | null = null;
  const lines = body.split('\n').map(line => {
    const fenceMatch = line.match(fencePattern);
    if (fence) {
      if (fenceMatch && fenceMatch[1].startsWith(fence)) {
        fence = null;
      }
      return line;
    }
    if (fenceMatch) {
      fence = fenceMatch[1];
      return line;
    }
    if (unwrappableLinePattern.test(line)) {
      return line;
    }
    const match = line.match(blockPrefixPattern);
    return match && match[2] ? match[1] + wrap(match[2]) + match[3] : line;
  });
  return leading + lines.join('\n') + trailing;
}

export function sanitizedLinkTitle(content: string): string {
  const sanitized = sanitizeWhitespace(content);
  return sanitized
    .replace(/[\t\r\n]+/g, ' ');
}

// To handle code that is presented as below (see https://github.com/laurent22/joplin/issues/573)
//
// <td class="code">
//   <pre class="python">
//     <span style="color: #ff7700;font-weight:bold;">def</span> ma_fonction
//   </pre>
// </td>
export function isCodeBlockSpecialCase1(node: Node): boolean {
  const parent = node.parentNode
  if (!parent) {
    return false;
  }
  return parent.nodeType === NodeTypes.Element
    && (parent as Element).classList
    && (parent as Element).classList.contains('code')
    && parent.nodeName === 'TD' && node.nodeName === 'PRE'
}

// To handle PRE tags that have a monospace font family. In that case
// we assume it is a code block.
export function isCodeBlockSpecialCase2(node: Node): boolean {
  if (node.nodeName !== 'PRE') {
    return false;
  }
  if (node.nodeType !== NodeTypes.Element) {
    return false;
  }
  const style = (node as Element).getAttribute('style');
  if (!style) {
    return false;
  }
  const o = CSSTools.parse('pre {' + style + '}');
  if (!o.stylesheet.rules.length) {
    return false;
  }
  const rule = o.stylesheet.rules[0];
  // Check if 'declarations' exists on the rule before accessing it
  if (!('declarations' in rule) || !rule.declarations) {
    return false;
  }
  const fontFamily = rule.declarations.find(d => {
    if (!('property' in d)) {
      return false;
    }
    return d.property.toLowerCase() === 'font-family';
  });
  if (!fontFamily
    || ('value' in fontFamily && !fontFamily.value)) {
    return false;
  }
  const isMonospace = 'value' in fontFamily
    && fontFamily.value.split(',').map(e => e.trim().toLowerCase()).indexOf('monospace') >= 0;
  return isMonospace;
}

export function isCodeBlock(node: Node): boolean {
  if (isCodeBlockSpecialCase1(node)
    || isCodeBlockSpecialCase2(node)) {
    return true
  }

  return (
    node.nodeName === 'PRE' &&
    node.firstChild &&
    node.firstChild.nodeName === 'CODE'
  ) || false;
}

export function isWhitespacePreserved(node: Node): boolean {
  if (node.nodeName === 'PRE') {
    return true;
  }
  const value = getInlineStyleProperty(node, 'white-space');
  if (!value) {
    return false;
  }
  return ['pre', 'pre-wrap', 'pre-line', 'break-spaces'].includes(value);
}

export type RequireOnly<T, K extends keyof T> =
  T & Required<Pick<T, K>>;
